import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowRight, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Inline premium gate — shown instead of locked content for free users.
 * Two variants: "full" (full-page gate) and "card" (compact teaser card).
 */
export function PremiumGate({ feature, description, variant = "full" }) {
  if (variant === "card") {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-spa-border bg-white/80 p-6 group" data-testid={`premium-gate-card-${feature?.toLowerCase().replace(/\s/g, '-')}`}>
        {/* Blur overlay */}
        <div className="absolute inset-0 backdrop-blur-[2px] bg-white/40 z-10 flex flex-col items-center justify-center text-center p-4">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <p className="font-serif text-sm text-spa-moss font-medium mb-1">{feature}</p>
          <p className="text-[11px] text-spa-text-secondary mb-3">{description || "Available with Premium"}</p>
          <Link
            to="/pricing"
            className="px-4 py-1.5 bg-spa-terracotta text-white rounded-full text-xs font-medium hover:bg-spa-terracotta/90 transition-colors inline-flex items-center gap-1"
            data-testid="premium-gate-upgrade-btn"
          >
            Upgrade <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {/* Ghost content behind blur */}
        <div className="opacity-30 pointer-events-none">
          <div className="w-12 h-12 bg-spa-muted rounded-xl mb-3" />
          <div className="h-4 bg-spa-muted rounded w-3/4 mb-2" />
          <div className="h-3 bg-spa-muted rounded w-1/2 mb-4" />
          <div className="h-20 bg-spa-muted rounded-xl" />
        </div>
      </div>
    );
  }

  // Full page gate
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-8" data-testid={`premium-gate-${feature?.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="max-w-lg text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-50 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
          <Crown className="w-10 h-10 text-amber-500" strokeWidth={1.5} />
        </div>
        <h2 className="font-serif text-3xl text-spa-moss mb-3">
          Unlock {feature}
        </h2>
        <p className="text-spa-text-secondary mb-8 leading-relaxed max-w-md mx-auto">
          {description || `${feature} is a premium feature. Upgrade your plan to access AI coaching, streaming, community forums, and the full SMAHOO wellness ecosystem.`}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/pricing" data-testid="premium-gate-upgrade-btn">
            <Button className="spa-button-primary px-8 py-3 gap-2">
              <Sparkles className="w-4 h-4" /> View Premium Plans
            </Button>
          </Link>
          <Link
            to="/dashboard"
            className="text-sm text-spa-text-secondary hover:text-spa-moss transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-3 gap-4 max-w-sm mx-auto">
          {["AI Coaching", "Streaming", "Community", "Calendar", "Mood Tracker", "Life Skills"].map(f => (
            <div key={f} className="text-center">
              <div className="w-8 h-8 bg-spa-muted rounded-lg flex items-center justify-center mx-auto mb-1">
                <Lock className="w-3.5 h-3.5 text-spa-text-secondary" />
              </div>
              <p className="text-[10px] text-spa-text-secondary">{f}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Utility: check if user has premium access
 */
export function isPremium(user) {
  if (!user) return false;
  const paidTiers = ["basic", "premium", "elite", "retreat", "admin"];
  return paidTiers.includes(user.subscription_tier);
}

/**
 * Utility: check if user is admin (admins always have full access)
 */
export function isAdmin(user) {
  if (!user) return false;
  const adminRoles = ["owner", "super_admin", "admin", "moderator", "support"];
  return adminRoles.includes(user.role);
}

/**
 * Combined check — premium OR admin
 */
export function hasFullAccess(user) {
  return isPremium(user) || isAdmin(user);
}
